import type {
  GameBookImportRequest,
  GameBookImportReceipt,
} from "../../../../shared/contracts/gamebook-import.js";
import type { RoundJournal } from "../../../data/round-journal.js";

export async function executeServerImport(options: {
  accountId: string;
  request: GameBookImportRequest;
  journal: RoundJournal;
  access(): void;
  post(input: GameBookImportRequest): Promise<GameBookImportReceipt>;
}): Promise<GameBookImportReceipt> {
  const { accountId, journal } = options;
  options.access();
  const existing = await journal.getLatestRecoverableSnapshot(accountId);
  if (
    (await journal.hasUnsynced(accountId)) ||
    (existing && existing.roundId !== options.request.requestId)
  )
    throw new Error(
      "Finish syncing and close your open round before importing.",
    );
  options.access();
  const receipt = await options
    .post(structuredClone(options.request))
    .catch((error: unknown) => {
      if (error instanceof TypeError || error instanceof SyntaxError)
        throw new Error(
          "Connection interrupted. Continue the saved import to confirm the same server result.",
        );
      throw error;
    });
  options.access();
  if (
    receipt.snapshot.roundId !== receipt.roundId ||
    (!(receipt.snapshot.state === "completed" && receipt.snapshot.result) &&
      !(
        options.request.completion === "review" &&
        receipt.snapshot.state === "active"
      ))
  )
    throw new Error(
      "Open the existing imported round to review its current result.",
    );
  const opened = await journal.getLatestRecoverableSnapshot(accountId);
  if (
    (await journal.hasUnsynced(accountId)) ||
    (opened && opened.roundId !== receipt.roundId)
  )
    throw new Error(
      "The import is saved on the server. Finish your open round, then continue this saved import.",
    );
  options.access();
  await journal.acceptLiveSnapshot(accountId, receipt.snapshot);
  options.access();
  return receipt;
}
